import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  FileWarning,
  Files,
  Clock4,
  ClipboardX,
  RefreshCw,
  TrendingUp,
  ShieldCheck,
  PackageCheck,
  TriangleAlert,
  Sparkles,
} from "lucide-react";

const BEFORE = [
  {
    icon: FileWarning,
    title: "Phantom inventory",
    body: "Discrepancies between what the system says and what is actually on the shelf.",
  },
  {
    icon: Files,
    title: "Excel version chaos",
    body: "Critical BOM and stock data trapped in conflicting spreadsheet copies.",
  },
  {
    icon: Clock4,
    title: "Delayed assemblies",
    body: "Production stalls waiting on parts nobody knew were short.",
  },
  {
    icon: ClipboardX,
    title: "Manual PO stress",
    body: "Reorder decisions made by gut feel instead of consumption data.",
  },
];

const AFTER = [
  {
    icon: RefreshCw,
    title: "Structured inventory lifecycle",
    body: "Receiving, kitting, consumption and replenishment governed by one system of record.",
  },
  {
    icon: TrendingUp,
    title: "Accurate demand forecasting",
    body: "Historic consumption trends and part usage rates drive what to buy — and when.",
  },
  {
    icon: ShieldCheck,
    title: "Right-sized safety stock",
    body: "Optimal thresholds protect production output without tying up cash.",
  },
  {
    icon: PackageCheck,
    title: "Repeatable processes",
    body: "Disciplined receiving, counting and fulfillment workflows that audit cleanly.",
  },
];

function Panel({ side, active, onActivate, items }) {
  const isActive = active === side;
  const isBefore = side === "before";
  return (
    <motion.article
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7 }}
      onMouseEnter={() => onActivate(side)}
      onClick={() => onActivate(side)}
      data-testid={`value-panel-${side}`}
      className={`relative h-full cursor-pointer rounded-2xl border p-6 sm:p-8 bg-[#111827]/70 backdrop-blur transition-all duration-500 ${
        isActive
          ? isBefore
            ? "border-[#F59E0B]/40 shadow-[0_0_60px_rgba(245,158,11,0.07)]"
            : "border-[#00F0FF]/40 shadow-[0_0_60px_rgba(0,240,255,0.09)]"
          : "border-white/[0.07] opacity-45"
      }`}
    >
      <div className="flex items-center gap-3 pb-5 border-b border-white/[0.07]">
        {isBefore ? (
          <TriangleAlert size={17} className="text-[#F59E0B]" />
        ) : (
          <Sparkles size={17} className="text-[#00F0FF]" />
        )}
        <h3
          className={`font-mono text-[11px] uppercase tracking-[0.26em] ${
            isBefore ? "text-[#F59E0B]" : "text-[#00F0FF]"
          }`}
        >
          {isBefore ? "Before — The Spreadsheet Bottleneck" : "After — The MRP Discipline"}
        </h3>
      </div>
      <ul className="pt-6 space-y-6">
        {items.map((it) => (
          <li key={it.title} className="flex gap-4">
            <span
              className={`mt-0.5 h-9 w-9 shrink-0 rounded-lg flex items-center justify-center border ${
                isBefore
                  ? "border-[#F59E0B]/25 bg-[#F59E0B]/[0.07] text-[#F59E0B]"
                  : "border-[#00F0FF]/25 bg-[#00F0FF]/[0.07] text-[#00F0FF]"
              }`}
            >
              <it.icon size={16} />
            </span>
            <div>
              <p className="font-display font-semibold text-white text-[15px]">{it.title}</p>
              <p className="mt-1 text-sm text-[#9CA3AF] leading-relaxed">{it.body}</p>
            </div>
          </li>
        ))}
      </ul>
    </motion.article>
  );
}

export default function ValueProp() {
  const [active, setActive] = useState("after");
  const [auto, setAuto] = useState(true);

  useEffect(() => {
    if (!auto) return;
    const t = setInterval(
      () => setActive((a) => (a === "before" ? "after" : "before")),
      3800
    );
    return () => clearInterval(t);
  }, [auto]);

  const activate = (side) => {
    setAuto(false);
    setActive(side);
  };

  return (
    <section id="value" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#00F0FF]">
            01 — The Problem-Solver
          </p>
          <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.1]">
            From spreadsheet chaos to planning discipline.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#9CA3AF] leading-relaxed">
            I guide growing businesses away from error-prone manual tracking and
            legacy accounting-only setups into structured inventory lifecycle
            management, accurate demand forecasting, and disciplined material planning.
          </p>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-8">
          <Panel side="before" active={active} onActivate={activate} items={BEFORE} />
          <Panel side="after" active={active} onActivate={activate} items={AFTER} />
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-10 font-mono text-[11px] uppercase tracking-[0.22em] text-[#6B7280] text-center"
        >
          Legacy tools aren't the enemy — stale data is. The migration is where I come in.
        </motion.p>
      </div>
    </section>
  );
}
